import {
  Checkbox as CommonCheckBox,
  DatePicker as FabricDatePicker,
  ICheckboxProps,
  IDatePickerProps,
  IDatePickerStrings,
  IDropdownStyles,
  ILabelStyles,
  ITextFieldStyles,
  Label
} from 'office-ui-fabric-react';
import React from 'react';

import { Error, IFormComponentProps } from '../../../models/app.models';
import { ErrorCodes, FormFieldOptionV2, FormFieldV2 } from '../../../models/safDiagnostic.models';
import { createStructuredSelector, IImmutableMap, mconnect } from '../../../state/redux';
import { IStyledComponent, styledComponent } from '../../../theme/styledComponent';
import { IConnectedComponentProps } from '../../../types';
import { validatorWrapper } from '../../../utils/safDiagnosticSchema';
import { dispatchResetErrorAction, dispatchSetErrorAction } from '../../reducers/dispatchers';
import { errorSelector } from '../../reducers/selectors.redux';

import * as strings from './common.string.json';
import { getFormDatePickerLabelStyles, getFormDropDownStyles, getFormTextFieldStyles } from './common.styles';
import {
  Dropdown as CommonDropDown,
  IDropdownOption as ICommonDropdownOption,
  IDropdownProps as ICommonDropdownProps
} from './Dropdown';
import { ITextFieldProps as CommonTextFieldProps, TextField as CommonTextField } from './TextField';

const mapStateToProps = createStructuredSelector({ error: errorSelector });
const mapDispatchToProps = { dispatchResetErrorAction, dispatchSetErrorAction };

type IFormTextFieldProps = CommonTextFieldProps &
  IFormComponentProps &
  ReturnType<typeof mapStateToProps> &
  IStyledComponent<ITextFieldStyles> &
  typeof mapDispatchToProps &
  IConnectedComponentProps;

type IFormCheckBoxProps = ICheckboxProps &
  IFormComponentProps &
  ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  IConnectedComponentProps;

type IFormDropdownProps = ICommonDropdownProps &
  IFormComponentProps &
  ReturnType<typeof mapStateToProps> &
  IStyledComponent<IDropdownStyles> &
  typeof mapDispatchToProps &
  IConnectedComponentProps;

type IFormDatePickerProps = IDatePickerProps &
  IFormComponentProps &
  ReturnType<typeof mapStateToProps> &
  IStyledComponent<ILabelStyles> &
  typeof mapDispatchToProps &
  IConnectedComponentProps;

const ErrorMessages: { [key: string]: string } = {
  DiagnosticsFormRequiredFieldError: 'Field is required.',
  DiagnosticsFormNumberBoundaryError: 'Input needs to between {lower_bound} and {upper_bound}.',
  DiagnosticsFormStringLengthError: 'Input cannot be longer than {in_value}.',
  DiagnosticsFormStringMaxLengthError: 'Input cannot be longer than {in_value} characters.',
  DiagnosticsFormStringMinLengthError: 'Input cannot be shorter than {in_value} characters.',
  DiagnosticsFormStringPatternError: 'Input does not match pattern.'
};
const getComponentErrorLocMessage = (
  error: IImmutableMap<Error> | null,
  componentModel: IImmutableMap<FormFieldV2>
) => {
  if (!!error && error.get('ErrorCode')) {
    if (error.get('ErrorCode') === ErrorCodes.DiagnosticsFormStringLengthError) {
      if (componentModel.get('Constraints')) {
        const maxLengthConstraint = componentModel.get('Constraints').get('MaxLength');
        const minLengthConstraint = componentModel.get('Constraints').get('MinLength');
        const value = componentModel.get('Value');
        if (value && typeof value === 'string') {
          if (maxLengthConstraint !== undefined && value.length > maxLengthConstraint) {
            return ErrorMessages.DiagnosticsFormStringMaxLengthError.replace(
              '{in_value}',
              maxLengthConstraint.toString()
            );
          } else if (minLengthConstraint !== undefined && value.length < minLengthConstraint) {
            return ErrorMessages.DiagnosticsFormStringMinLengthError.replace(
              '{in_value}',
              minLengthConstraint.toString()
            );
          }
        }
      }
      return '';
    }
    if (error.get('ErrorCode') === ErrorCodes.DiagnosticsFormNumberBoundaryError) {
      if (
        componentModel.get('Constraints') &&
        componentModel.get('Constraints').get('LowerBound') &&
        componentModel.get('Constraints').get('UpperBound')
      ) {
        return ErrorMessages[error.get('ErrorCode')]
          .replace(
            '{lower_bound}',
            componentModel
              .get('Constraints')
              .get('LowerBound')
              .toString()
          )
          .replace(
            '{upper_bound}',
            componentModel
              .get('Constraints')
              .get('UpperBound')
              .toString()
          );
      }
    }
    return ErrorMessages[error.get('ErrorCode')];
  }
  return '';
};

export class TextFieldC extends React.PureComponent<IFormTextFieldProps> {
  public render(): React.ReactNode {
    const { componentModel } = this.props;
    const constraints = componentModel.get('Constraints');
    /** For better user experience if the text field has a
     * placeholder we use it for the tool tip as this
     * helps the user see the standard for the field as they keep on typing*/
    return (
      <CommonTextField
        title={componentModel.get('LocalizedPlaceholder') || componentModel.get('LocalizedToolTip')}
        label={componentModel.get('Title')}
        placeholder={componentModel.get('LocalizedPlaceholder')}
        onChange={this._onChange}
        value={componentModel.get('Value') ? componentModel.get('Value') : ''}
        multiline={componentModel.get('IsMultilineText')}
        rows={componentModel.get('MultilineRowsCount')}
        required={constraints ? constraints.get('Required') : false}
        validateOnFocusOut={true}
        errorMessage={getComponentErrorLocMessage(this.props.error, componentModel)}
        pattern={constraints ? constraints.get('Pattern') : ''}
        styles={this.props.styles || getFormTextFieldStyles(this.props)}
        onBlur={this._onBlur}
      />
    );
  }

  private _onBlur = (_ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validatorWrapper(
      this.props.componentModel.get('Value'),
      this.props.componentModel,
      this.props.onChangeReducer,
      this.props.dispatchSetErrorAction,
      this.props.dispatchResetErrorAction,
      _ev.type
    );
  };

  private _onChange = (_ev: React.FormEvent<HTMLDivElement | HTMLTextAreaElement>, data?: string | undefined) => {
    validatorWrapper(
      data,
      this.props.componentModel,
      this.props.onChangeReducer,
      this.props.dispatchSetErrorAction,
      this.props.dispatchResetErrorAction,
      _ev.type
    );
  };
}

export class CheckboxC extends React.PureComponent<IFormCheckBoxProps> {
  public render(): React.ReactNode {
    const { componentModel } = this.props;

    return (
      <CommonCheckBox
        label={componentModel.get('Title')}
        placeholder={componentModel.get('LocalizedPlaceholder')}
        title={componentModel.get('LocalizedToolTip')}
        checked={componentModel.get('Value')}
        onChange={this._onChange}
      />
    );
  }

  private _onChange = (
    _ev: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
    checked: boolean | undefined
  ) => {
    validatorWrapper(
      checked,
      this.props.componentModel,
      this.props.onChangeReducer,
      this.props.dispatchSetErrorAction,
      this.props.dispatchResetErrorAction,
      _ev!.type
    );
  };
}

class DropdownC extends React.PureComponent<IFormDropdownProps> {
  private _options: ICommonDropdownOption[] = [];

  constructor(props: IFormDropdownProps) {
    super(props);
    this._options = this._generateOptions();
  }

  public render(): React.ReactNode {
    const { componentModel } = this.props;
    const constraints = componentModel.get('Constraints');

    return (
      <CommonDropDown
        stylesFunc={this.props.stylesFunc}
        callbackHandlers={this.props.callbackHandlers}
        title={componentModel.get('LocalizedToolTip')}
        logValue={false}
        placeHolder={componentModel.get('LocalizedPlaceholder')}
        label={componentModel.get('Title')}
        onChange={this._onChange}
        options={this._options}
        required={constraints ? constraints.get('Required') : false}
        errorMessage={getComponentErrorLocMessage(this.props.error, componentModel)}
        styles={getFormDropDownStyles(this.props)}
        selectedKey={this._getSelectedKey()}
      />
    );
  }

  private _onChange = (_ev: React.FormEvent<HTMLDivElement>, option: ICommonDropdownOption) => {
    if (option) {
      validatorWrapper(
        option.data,
        this.props.componentModel,
        this.props.onChangeReducer,
        this.props.dispatchSetErrorAction,
        this.props.dispatchResetErrorAction,
        _ev.type
      );
    }
  };

  private _getSelectedKey = () => {
    if (this.props.componentModel.get('Value')) {
      const selectedOption = this._options.find((option: ICommonDropdownOption) => {
        return option.data === this.props.componentModel.get('Value');
      });
      return selectedOption!.key;
    }
    return undefined;
  };

  private _generateOptions = () => {
    return this.props.componentModel
      .get('AnyOf')
      .map((option: IImmutableMap<FormFieldOptionV2>) => {
        return {
          key: option.get('Key'),
          text: option.get('LocalizedTitle'),
          title: option.get('LocalizedTitle'),
          data: option.get('Value')
        } as ICommonDropdownOption;
      })
      .toArray();
  };
}

class DatePickerC extends React.PureComponent<IFormDatePickerProps> {
  public render(): React.ReactNode {
    const { componentModel } = this.props;
    const constraints = componentModel.get('Constraints');
    const datePickerStrings: IDatePickerStrings = {
      months: [
        strings.DatePickerStrings.months.January,
        strings.DatePickerStrings.months.February,
        strings.DatePickerStrings.months.March,
        strings.DatePickerStrings.months.April,
        strings.DatePickerStrings.months.May,
        strings.DatePickerStrings.months.June,
        strings.DatePickerStrings.months.July,
        strings.DatePickerStrings.months.August,
        strings.DatePickerStrings.months.September,
        strings.DatePickerStrings.months.October,
        strings.DatePickerStrings.months.November,
        strings.DatePickerStrings.months.December
      ],

      shortMonths: [
        strings.DatePickerStrings.shortMonths.Jan,
        strings.DatePickerStrings.shortMonths.Feb,
        strings.DatePickerStrings.shortMonths.Mar,
        strings.DatePickerStrings.shortMonths.Apr,
        strings.DatePickerStrings.shortMonths.May,
        strings.DatePickerStrings.shortMonths.Jun,
        strings.DatePickerStrings.shortMonths.Jul,
        strings.DatePickerStrings.shortMonths.Aug,
        strings.DatePickerStrings.shortMonths.Sep,
        strings.DatePickerStrings.shortMonths.Oct,
        strings.DatePickerStrings.shortMonths.Nov,
        strings.DatePickerStrings.shortMonths.Dec
      ],

      days: [
        strings.DatePickerStrings.days.Sunday,
        strings.DatePickerStrings.days.Monday,
        strings.DatePickerStrings.days.Tuesday,
        strings.DatePickerStrings.days.Wednesday,
        strings.DatePickerStrings.days.Thursday,
        strings.DatePickerStrings.days.Friday,
        strings.DatePickerStrings.days.Saturday
      ],

      shortDays: [
        strings.DatePickerStrings.shortDays.Sunday,
        strings.DatePickerStrings.shortDays.Monday,
        strings.DatePickerStrings.shortDays.Tuesday,
        strings.DatePickerStrings.shortDays.Wednesday,
        strings.DatePickerStrings.shortDays.Thursday,
        strings.DatePickerStrings.shortDays.Friday,
        strings.DatePickerStrings.shortDays.Saturday
      ],
      goToToday: strings.DatePickerStrings.goToToday,
      prevMonthAriaLabel: strings.DatePickerStrings.prevMonthAriaLabel,
      nextMonthAriaLabel: strings.DatePickerStrings.nextMonthAriaLabel,
      prevYearAriaLabel: strings.DatePickerStrings.prevYearAriaLabel,
      nextYearAriaLabel: strings.DatePickerStrings.nextYearAriaLabel,
      closeButtonAriaLabel: strings.DatePickerStrings.closeButtonAriaLabel,
      isRequiredErrorMessage: strings.DatePickerStrings.isRequiredErrorMessage,
      invalidInputErrorMessage: strings.DatePickerStrings.invalidInputErrorMessage
    };

    return (
      <>
        <Label styles={getFormDatePickerLabelStyles(this.props)}>{componentModel.get('Title')}</Label>
        <FabricDatePicker
          placeholder={componentModel.get('LocalizedPlaceholder')}
          title={componentModel.get('LocalizedToolTip')}
          isRequired={constraints.get('Required')}
          onSelectDate={this._onSelectDate}
          value={componentModel.get('Value') ? componentModel.get('Value') : ''}
          showMonthPickerAsOverlay={true}
          strings={datePickerStrings}
          allowTextInput={true}
          minDate={constraints.get('MinDate') ? constraints.get('MinDate').toDate() : undefined}
          maxDate={constraints.get('MaxDate') ? constraints.get('MaxDate').toDate() : undefined}
        />
      </>
    );
  }

  private _onSelectDate = (date: Date | null | undefined) => {
    validatorWrapper(
      date,
      this.props.componentModel,
      this.props.onChangeReducer,
      this.props.dispatchSetErrorAction,
      this.props.dispatchResetErrorAction,
      ''
    );
  };
}

export const Checkbox = mconnect(mapStateToProps, mapDispatchToProps)(CheckboxC);
// tslint:disable-next-line: deprecation
export const DatePicker = mconnect(
  mapStateToProps,
  mapDispatchToProps
)(styledComponent(DatePickerC, getFormDatePickerLabelStyles));
// tslint:disable-next-line: deprecation
export const Dropdown = mconnect(
  mapStateToProps,
  mapDispatchToProps
)(styledComponent(DropdownC, getFormDropDownStyles));
// tslint:disable-next-line: deprecation
export const TextField = mconnect(
  mapStateToProps,
  mapDispatchToProps
)(styledComponent(TextFieldC, getFormTextFieldStyles));
